<script setup lang="ts">
/**
 * DeleteChoiceDialog
 *
 * Fragt beim Löschen auf der Eingabe-Vorschau, ob ganz gelöschte Zählzeiten zur Pause werden oder
 * die folgenden Noten der Stimme nachrücken. Geöffnet wird er ausschließlich über
 * `requestDelete` (Zustand `deleteChoicePending` im `ScoreEditContext`); wann er erscheint,
 * entscheidet `useScoreEditActions.requestDelete`.
 *
 * **Tasten im Capture-Listener mit `stopImmediatePropagation` (kritisch – nicht auf einen
 * normalen Listener rückbauen):** die Kürzel der Vorschau (Pfeile, Entf, Q/W, Leertaste …) hängen
 * als `onKeyStroke` am `window` in der Bubble-Phase. Ohne Abfangen liefen sie hinter dem
 * modalen Dialog weiter – ein Pfeil transponierte die noch ausgewählten Noten.
 */
import {nextTick, ref, watch} from 'vue';
import {Icon} from '@iconify/vue';
import {useEventListener} from '@vueuse/core';
import {injectScoreEditContext} from './editContext';
import {focusWithoutScroll} from '@/shared/focus';

const {deleteChoicePending, resolveDeleteChoice} = injectScoreEditContext();

const restButton = ref<HTMLButtonElement | null>(null);

watch(deleteChoicePending, async (open) => {
  if (!open) return;
  await nextTick();
  focusWithoutScroll(restButton.value);
});

useEventListener(window, 'keydown', (e: KeyboardEvent) => {
  if (!deleteChoicePending.value) return;
  e.stopImmediatePropagation();
  const key = e.key.toLowerCase();
  if (key === 'escape') {
    e.preventDefault();
    resolveDeleteChoice(null);
  } else if (key === 'enter' || key === 'p') {
    e.preventDefault();
    resolveDeleteChoice('rest');
  } else if (key === 'n') {
    e.preventDefault();
    resolveDeleteChoice('shift');
  }
}, {capture: true});
</script>

<template>
  <div
      v-if="deleteChoicePending"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      @click.self="resolveDeleteChoice(null)"
  >
    <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-choice-title"
        class="flex w-full max-w-md flex-col overflow-hidden rounded-xl bg-white shadow-xl animate-fade animate-duration-150"
    >
      <header class="flex items-center gap-2 border-b border-gray-200 px-5 py-3">
        <Icon icon="fa6-solid:trash-can" class="shrink-0 text-red-500"/>
        <h2 id="delete-choice-title" class="text-base font-semibold text-gray-900">Noten löschen</h2>
      </header>

      <p class="px-5 py-4 text-sm text-gray-700">
        Sollen die gelöschten Noten durch <strong>Pausen</strong> ersetzt werden, oder sollen die
        <strong>folgenden Noten nachrücken</strong>?
      </p>

      <footer class="flex flex-wrap justify-end gap-2 border-t border-gray-200 px-5 py-3">
        <button type="button" class="btn btn-secondary" @click="resolveDeleteChoice(null)">
          Abbrechen <kbd class="ml-1.5 text-xs text-gray-400">Esc</kbd>
        </button>
        <button type="button" class="btn btn-secondary" @click="resolveDeleteChoice('shift')">
          <Icon icon="fa6-solid:arrow-left" class="mr-1.5 text-sm"/> Nachrücken
          <kbd class="ml-1.5 text-xs text-gray-400">N</kbd>
        </button>
        <button ref="restButton" type="button" class="btn btn-primary" @click="resolveDeleteChoice('rest')">
          <Icon icon="fa6-solid:pause" class="mr-1.5 text-sm"/> Durch Pause ersetzen
          <kbd class="ml-1.5 text-xs opacity-70">Enter</kbd>
        </button>
      </footer>
    </div>
  </div>
</template>
